<template>
  <div class="order-details-wrapper">
    <SideSheet
      :is-open="isOpen"
      position="right"
      class="order-details-sidesheet"
      @close="$emit('close')"
    >
      <template v-if="orderItem">
        <OrderDetailsHero :order-item="orderItem" @close="$emit('close')" />
        <div
          v-if="shouldShowAlert"
          role="button"
          class="order-details-sidesheet__alert"
          @click="isRedemptionDetailsOpen = true"
        >
          <div class="order-details-sidesheet__alert__text">
            <BaseIcon
              :icon="alertDetails?.icon"
              :colors="NebraskaColors.elementPrimary"
            />
            <BaseText size="p4" :color="NebraskaColors.elementPrimary">
              {{ alertDetails?.title }}
            </BaseText>
          </div>
          <BackButton icon="EF0030" @click="showAlert = false" />
        </div>
        <OrderDetailsContent
          :order-item="orderItem"
          @click="handleProductClick"
        />
      </template>
    </SideSheet>
    <ProductDetailsModal
      :is-open="isProductDetailsModalOpen"
      :product="selectedProduct"
      @close="closeProductDetailsModal"
    />
    <RedemptionModal
      :is-open="isRedemptionDetailsOpen"
      :details="alertDetails"
      @close="isRedemptionDetailsOpen = false"
    />
  </div>
</template>
<script lang="ts" setup>
import { computed, ref } from 'vue';

import { BaseIcon, BaseText } from '@warrenbrasil/nebraska-web';
import { SideSheet } from '@/modules/common/components/SideSheet';
import { BackButton } from '@/modules/common/components/buttons/BackButton';
import {
  OrderDetailsHero,
  OrderDetailsContent,
  ProductDetailsModal,
  RedemptionModal
} from './components';
import {
  IOrderItem,
  IOrderProduct,
  EOrderStatus
} from '@/modules/wealth/services/portfolio-orders';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';

interface Props {
  isOpen: boolean;
  orderItem: IOrderItem | null;
}
const props = defineProps<Props>();

const isProductDetailsModalOpen = ref(false);
const selectedProduct = ref<IOrderProduct | null>(null);
const showAlert = ref(true);
const isRedemptionDetailsOpen = ref(false);

const alertDetails = computed(() => {
  return props.orderItem?.details.alert;
});

const shouldShowAlert = computed(() => {
  return Boolean(
    alertDetails.value &&
      showAlert.value &&
      props.orderItem?.details.header.status !== EOrderStatus.CANCELED
  );
});

function handleProductClick(product: IOrderProduct) {
  selectedProduct.value = product;
  isProductDetailsModalOpen.value = true;
}

function closeProductDetailsModal() {
  isProductDetailsModalOpen.value = false;
  selectedProduct.value = null;
}
</script>

<style lang="less" scoped>
.order-details-sidesheet {
  :deep(.panel-content.right) {
    width: 683px;
  }

  &__alert {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: @size-spacing-x300 @size-spacing-x1200;
    background-color: @background-tertiary;

    &:hover {
      cursor: pointer;
    }

    &__text {
      display: flex;
      align-items: center;
      gap: @size-spacing-x300;

      :deep(.p4) {
        line-height: 0;
      }
    }
  }
}
</style>
